import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API, authFetch } from "@/App";
import AlbumCard from "@/components/AlbumCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Users, Disc3, Loader2 } from "lucide-react";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [activeTab, setActiveTab] = useState(searchParams.get("type") || "albums");
  const [albums, setAlbums] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = searchParams.get("q");
    const type = searchParams.get("type") || "albums";
    if (q) {
      setQuery(q);
      setActiveTab(type);
      performSearch(q, type);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery, type) => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    setLoading(true);
    try {
      if (type === "albums" || type === "all") {
        const albumRes = await authFetch(
          `${API}/deezer/search?q=${encodeURIComponent(searchQuery)}&limit=24`
        );
        if (albumRes.ok) {
          const data = await albumRes.json();
          setAlbums(data);
        }
      }
      
      if (type === "users" || type === "all") {
        const userRes = await authFetch(
          `${API}/users/search?q=${encodeURIComponent(searchQuery)}&limit=20`
        );
        if (userRes.ok) {
          const data = await userRes.json();
          setUsers(data);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      setSearchParams({ q: query.trim(), type: activeTab });
      performSearch(query.trim(), activeTab);
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (query.trim().length >= 2) {
      setSearchParams({ q: query.trim(), type: newTab });
      performSearch(query.trim(), newTab);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="search-page">
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-purple-900/10 pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-white">
            Search
          </h1>
          <p className="text-white/60">
            Find albums and users on Sonary.
          </p>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                placeholder="Search albums or users..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-full text-lg focus:border-purple-500"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit" 
              className="rounded-full px-8 h-12"
              disabled={loading || query.trim().length < 2}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Search"}
            </Button>
          </div>
        </form>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="bg-white/5 p-1 rounded-full mb-8">
            <TabsTrigger 
              value="albums"
              className="rounded-full px-6 data-[state=active]:bg-purple-500 data-[state=active]:text-white"
            >
              <Disc3 className="w-4 h-4 mr-2" />
              Albums
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="rounded-full px-6 data-[state=active]:bg-purple-500 data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Albums Tab */}
          <TabsContent value="albums">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : albums.length > 0 ? (
              <>
                <p className="text-sm text-white/50 mb-4">{albums.length} albums found</p>
                <div className="album-grid">
                  {albums.map((album) => (
                    <AlbumCard key={album.album_id} album={album} />
                  ))}
                </div>
              </>
            ) : query && !loading ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Disc3 className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No albums found</h3>
                <p className="text-white/60">Try a different search term.</p>
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <Search className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">Search for albums</h3>
                <p className="text-white/60">Enter an album name, artist, or genre.</p>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : users.length > 0 ? (
              <>
                <p className="text-sm text-white/50 mb-4">{users.length} users found</p>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {users.map((user) => (
                    <Link
                      key={user.user_id}
                      to={`/profile/${user.user_id}`}
                      className="glass-card rounded-xl p-4 hover:border-purple-500/30 transition-colors flex items-center gap-4"
                      data-testid={`user-result-${user.user_id}`}
                    >
                      <Avatar className="h-14 w-14 border border-white/10">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-purple-500/20 text-purple-400 text-lg">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">{user.name}</h3>
                        <div className="flex gap-4 text-sm text-white/50">
                          <span>{user.log_count || 0} albums</span>
                          <span>{user.follower_count || 0} followers</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            ) : query && !loading ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Users className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">No users found</h3>
                <p className="text-white/60">Try a different name.</p>
              </div>
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                <Users className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-white">Search for users</h3>
                <p className="text-white/60">Find and follow other music lovers.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
